/**
 * ExtensionReconnectModal — diferencia uma extensão conhecida cujo content script foi
 * invalidado por update/restart de uma instalação realmente ausente. A recarga reinjeta o
 * script; a rota e o formulário persistido da captura são preservados.
 * Visual da proposta "Elements" (17/10/2026): com captura rodando, o aviso leva à captura
 * ("Abrir a captura") e o "Recarregar o painel" fica travado.
 * Movimento (23/10/2026): a janela entra e sai com .ds-veu/.ds-dialogo (sempre renderizada; a
 * ScreenDialog monta ao abrir e fica montada enquanto sai).
 */
import { reloadPanelForExtensionReconnect } from '../../lib/extensionReconnect';
import { useCaptureStore } from '../../stores/captureStore';
import { useUiStore } from '../../stores/uiStore';
import { EXTENSION_RECONNECT_MODAL_ID } from '../../hooks/useRequireExtension';
import { ScreenDialog } from '../screen/Screen';
import { useScreenText } from '../screen/screenText';

export function ExtensionReconnectModal(): JSX.Element | null {
  const { tx } = useScreenText();
  const activeModal = useUiStore((state) => state.activeModal);
  const closeModal = useUiStore((state) => state.closeModal);
  const active = useCaptureStore((state) => state.active);
  const phase = useCaptureStore((state) => state.phase);
  const module = useCaptureStore((state) => state.module);
  const captureWindow = useCaptureStore((state) => state.captureWindow);
  const openCaptureWindow = useCaptureStore((state) => state.openCaptureWindow);
  const captureRunning = active || phase === 'running';

  return (
    <ScreenDialog
      open={activeModal === EXTENSION_RECONNECT_MODAL_ID}
      icon="sync"
      iconTone="teal"
      title={tx('rec_title')}
      closeLabel={tx('close')}
      onClose={closeModal}
    >
      <p className="as-dialog__text">{tx('rec_text')}</p>
      {captureRunning ? (
        <div className="as-note as-note--info" role="status">
          <span className="material-symbols-rounded" aria-hidden="true">
            info
          </span>
          <div>
            <strong>{tx('rec_capture_title')}</strong>
            <span>{tx('rec_capture_text')}</span>
            {captureWindow ? null : (
              <button
                type="button"
                className="as-btn as-btn--outline as-btn--sm ds-resposta"
                onClick={() => {
                  closeModal();
                  openCaptureWindow(module);
                }}
              >
                <span className="material-symbols-rounded" aria-hidden="true">
                  bolt
                </span>
                {tx('rec_open')}
              </button>
            )}
          </div>
        </div>
      ) : null}
      <div className="as-dialog__actions">
        <button
          type="button"
          className="as-btn as-btn--primary as-btn--md ds-resposta"
          disabled={captureRunning}
          onClick={() => {
            reloadPanelForExtensionReconnect({ active, phase, captureWindow });
          }}
        >
          <span className="material-symbols-rounded" aria-hidden="true">
            {captureRunning ? 'lock' : 'refresh'}
          </span>
          {tx('rec_reload')}
        </button>
      </div>
    </ScreenDialog>
  );
}
